import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTask, getTeams, getBrands, getUsers, aiSummarize, aiSuggestPriority, aiSuggestTags } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Lightning, Sparkle } from '@phosphor-icons/react';

export default function CreateTaskPage() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [brands, setBrands] = useState([]);
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState('');

  const [form, setForm] = useState({
    title: '', description: '', task_type: 'Feature', priority: 'Medium',
    status: 'Backlog',
    assigned_to: '', assigned_team: '', brand_id: '', domain: 'Other',
    custom_domain: '', deadline: '', figma_link: '', gdocs_link: ''
  });

  useEffect(() => {
    Promise.all([getTeams(), getBrands(), getUsers()]).then(([t, b, u]) => {
      setTeams(t.data); setBrands(b.data); setUsers(u.data);
    }).catch(err => console.error('Load form data failed:', err.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.assigned_to) payload.assigned_to = null;
      if (!payload.assigned_team) payload.assigned_team = null;
      if (!payload.brand_id) payload.brand_id = null;
      if (!payload.deadline) payload.deadline = null;
      if (!payload.figma_link) payload.figma_link = null;
      if (!payload.gdocs_link) payload.gdocs_link = null;
      const res = await createTask(payload);
      navigate(`/tasks/${res.data.id}`);
    } catch (e) {
      console.error(e);
      alert('Failed to create task');
    }
    setSubmitting(false);
  };

  const handleAISuggest = async () => {
    if (!form.title.trim()) return;
    setAiLoading(true);
    setAiSummary('');
    try {
      const [summaryRes, priorityRes, tagsRes] = await Promise.all([
        aiSummarize(form.title, form.description),
        aiSuggestPriority(form.title, form.description),
        aiSuggestTags(form.title, form.description)
      ]);
      setAiSummary(summaryRes.data.summary);
      setForm(prev => ({
        ...prev,
        priority: priorityRes.data.priority || prev.priority,
        task_type: tagsRes.data.task_type || prev.task_type
      }));
    } catch (e) { console.error(e); }
    setAiLoading(false);
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="create-task-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#555555] hover:text-[#0F0F0F] mb-5 transition-colors" data-testid="back-btn">
        <ArrowLeft size={16} />
        Back
      </button>

      <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F] mb-6">Create New Task</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Title *</Label>
          <Input data-testid="task-title-input" value={form.title} onChange={update('title')} placeholder="Task title" className="border-[#E8E8E8] rounded-md h-10" required />
        </div>

        {/* Description */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Description</Label>
          <Textarea data-testid="task-description-input" value={form.description} onChange={update('description')} placeholder="Detailed description..." className="min-h-[120px] border-[#E8E8E8] rounded-md resize-none" />
        </div>

        {/* AI Suggestions */}
        <div className="border border-[#32B4AC] rounded-md p-4 bg-[#32B4AC]/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#32B4AC] flex items-center gap-1.5">
              <Sparkle size={14} weight="fill" /> AI Assist
            </span>
            <button
              type="button"
              data-testid="ai-suggest-btn"
              onClick={handleAISuggest}
              disabled={aiLoading || !form.title.trim()}
              className="text-xs px-3 py-1.5 bg-[#32B4AC] text-white rounded-md hover:bg-[#2A9B94] disabled:opacity-50 font-medium"
            >
              {aiLoading ? 'Analyzing...' : 'Get AI Suggestions'}
            </button>
          </div>
          {aiSummary && (
            <p className="text-sm text-[#1A1A1A] leading-relaxed" data-testid="ai-summary">{aiSummary}</p>
          )}
          {!aiSummary && <p className="text-xs text-[#555555]">Enter a title and description, then click to get AI-powered priority, type, and summary suggestions.</p>}
        </div>

        {/* Row: Type + Priority + Status */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Task Type</Label>
            <select data-testid="task-type-select" value={form.task_type} onChange={update('task_type')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
              {['Bug', 'Feature', 'UI Change', 'Experiment', 'Integration', 'Other'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Priority</Label>
            <select data-testid="task-priority-select" value={form.priority} onChange={update('priority')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Status</Label>
            <select data-testid="task-status-create-select" value={form.status} onChange={update('status')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
              {['New Idea Repository', 'Backlog', 'In Discussion', 'In Design', 'In Development', 'In QA', 'Blocked', 'Completed'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Row: Team + Assigned To */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Assign to Team</Label>
            <select data-testid="task-team-select" value={form.assigned_team} onChange={update('assigned_team')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
              <option value="">Select team</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Assign to Person</Label>
            <select data-testid="task-assigned-to-select" value={form.assigned_to} onChange={update('assigned_to')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
              <option value="">Select person</option>
              {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
            </select>
          </div>
        </div>

        {/* Row: Brand + Domain */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Brand</Label>
            <select data-testid="task-brand-select" value={form.brand_id} onChange={update('brand_id')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
              <option value="">Select brand</option>
              {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Domain</Label>
            <select data-testid="task-domain-select" value={form.domain} onChange={update('domain')} className="w-full h-10 text-sm border border-[#E8E8E8] rounded-md px-3 bg-white">
              <option value="wellversed.in">wellversed.in</option>
              <option value="store.wellversed.in">store.wellversed.in</option>
              <option value="Both">Both</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        {form.domain === 'Other' && (
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Custom Domain</Label>
            <Input data-testid="custom-domain-input" value={form.custom_domain} onChange={update('custom_domain')} placeholder="e.g. youwefit.com" className="border-[#E8E8E8] rounded-md h-10" />
          </div>
        )}

        {/* Deadline */}
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Deadline</Label>
          <Input data-testid="task-deadline-input" type="date" value={form.deadline} onChange={update('deadline')} className="border-[#E8E8E8] rounded-md h-10 w-60" />
        </div>

        {/* Links */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Figma Link</Label>
            <Input data-testid="figma-link-input" value={form.figma_link} onChange={update('figma_link')} placeholder="https://figma.com/..." className="border-[#E8E8E8] rounded-md h-10" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Google Docs Link</Label>
            <Input data-testid="gdocs-link-input" value={form.gdocs_link} onChange={update('gdocs_link')} placeholder="https://docs.google.com/..." className="border-[#E8E8E8] rounded-md h-10" />
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[#E8E8E8]">
          <button type="button" onClick={() => navigate(-1)} className="h-10 px-5 text-sm border border-[#1A1A1A] text-[#0F0F0F] rounded-md hover:bg-[#F4F4F4] transition-colors" data-testid="cancel-btn">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !form.title.trim()}
            className="h-10 px-5 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="submit-task-btn"
          >
            {submitting ? 'Creating...' : 'Create Task'}
          </button>
        </div>
      </form>
    </div>
  );
}
